import React from 'react';
import { parseZone, toHumanTimeFormat } from '@common/services/datetime';
import { Box, Collapse, Flex, Gutter, Text, Table } from '@common/components';
import { formatNumberDigits } from '@common/services';
import { getShiftSourceDisplay } from '@scheduling/services';
import type { ShiftGapsByDayQueryCgResponse } from '@common/graphql/schema';

type ShiftGapType =
  ShiftGapsByDayQueryCgResponse['shiftGapsByDay']['shiftGaps'][number];

const COLUMNS = [
  {
    title: 'Time',
    dataIndex: 'time',
    key: 'time',
  },
  {
    title: 'Length',
    dataIndex: 'length',
    key: 'length',
  },
  {
    title: 'Openings',
    dataIndex: 'openings',
    key: 'openings',
  },
  {
    title: 'Source',
    dataIndex: 'source',
    key: 'source',
  },
];

export function ConflictShiftsBanner({
  shiftGaps,
  selectedIds,
  onSelectionChange,
}: {
  shiftGaps: ShiftGapType[];
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}) {
  if (shiftGaps.length === 0) return null;

  const dataSource = shiftGaps.map((gap) => {
    const startTime = parseZone(gap.startDatetime).tz(gap.timeZone);
    const endTime = parseZone(gap.endDatetime).tz(gap.timeZone);

    return {
      key: gap.id,
      time: `${toHumanTimeFormat(startTime)} - ${toHumanTimeFormat(endTime)}`,
      length: `${formatNumberDigits(Number(gap.shiftDuration))}hrs`,
      openings: (gap.openShiftGapOpenings ?? []).length,
      source: getShiftSourceDisplay(gap.source).label,
    };
  });

  const headerText = `When applied, this workload will cancel and replace ${shiftGaps.length} existing shift${shiftGaps.length === 1 ? '' : 's'}. Expand to review and choose which shifts to keep as needed.`;

  return (
    <Gutter size="md" direction="bottom">
      <Flex vertical align="stretch">
        <Box color="sunLighter">
          <Collapse>
            <Collapse.Panel key="conflicts" title={headerText}>
              <Gutter direction="bottom">
                <Text weight="medium">Select shifts to keep:</Text>
              </Gutter>
              <Table
                columns={COLUMNS}
                dataSource={dataSource}
                pagination={false}
                size="small"
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: Array.from(selectedIds),
                  onChange: (keys) =>
                    onSelectionChange(new Set(keys as string[])),
                }}
              />
            </Collapse.Panel>
          </Collapse>
        </Box>
      </Flex>
    </Gutter>
  );
}
